import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import patientService from '../../../../services/patientService';
import careManagementService from '../../../../services/careManagementService';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import html2pdf from 'html2pdf.js';

import EmailModal from '../../../../components/EmailModal/EmailModal';
import AddFollowUpModal from '../../../../components/AddFollowUpModal/AddFollowUpModal';
import BackButton from '../../../../components/BackButton/BackButton';
import ListScreenSearchBar from '../../../../components/Common/ListScreenSearchBar/ListScreenSearchBar';
import ListScreenPaginationBar from '../../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import './FollowUpList.css';

const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${month}-${day}-${year}/${String(displayHours).padStart(2, '0')}:${minutes} ${ampm}`;
};

const formatDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
};

const getServiceTypeDisplay = (serviceType) => {
    if (serviceType === null || serviceType === undefined || serviceType === '') return 'General';
    const raw = String(serviceType).trim().toLowerCase();
    if (raw === '1' || raw === 'general') return 'General';
    if (raw === '2' || raw === 'call via others') return 'Call via others';
    return String(serviceType);
};

const normalizeFollowUpStatusKey = (status) => {
    const normalized = String(status || '').toLowerCase().replace(/\s+/g, '_');
    if (!normalized) return 'continue_follow_up';
    if (['1', 'continue_follow_up', 'pending', 'in_progress'].includes(normalized)) {
        return 'continue_follow_up';
    }
    if (['0', 'no_need_to_follow_up', 'no_need', 'no_need_follow_up', 'completed', 'cancelled'].includes(normalized)) {
        return 'no_need_to_follow_up';
    }
    // Keep UI and filtering consistent: any non "no need" status is treated as continue.
    return 'continue_follow_up';
};

const renderFollowUpStatus = (status) => {
    return normalizeFollowUpStatusKey(status) === 'no_need_to_follow_up'
        ? 'No need to follow up'
        : 'Continue follow up';
};

const normalizeStatusForEdit = (status) => {
    const normalized = normalizeFollowUpStatusKey(status);
    return normalized === 'no_need_to_follow_up' ? 'no_need_to_follow_up' : 'continue_follow_up';
};

const toDateInputValue = (rawDate) => {
    if (!rawDate) return '';
    const d = new Date(rawDate);
    if (Number.isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const normalizeTimeInput = (raw) => {
    const value = String(raw || '').trim();
    if (!value) return '';
    if (/^\d{2}:\d{2}$/.test(value)) return value;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
};

const splitTimerValue = (row) => {
    const timer = String(row?.time || row?.timerValue || '').trim();
    if (timer.includes(':')) {
        const [m = '0', s = '00'] = timer.split(':');
        return {
            minutes: String(Number.parseInt(m, 10) || 0),
            seconds: String(Number.parseInt(s, 10) || 0).padStart(2, '0')
        };
    }
    const minutes = Number.parseInt(row?.service_time || 0, 10) || 0;
    return { minutes: String(minutes), seconds: '00' };
};

const FollowUpList = () => {
    const { practiceId, patientId } = useParams();
    const { updateBreadcrumbs } = useBreadcrumb();
    const [patientData, setPatientData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [results, setResults] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'date_time', direction: 'desc' });

    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailSending, setEmailSending] = useState(false);
    const [showAddFollowUpModal, setShowAddFollowUpModal] = useState(false);
    const fromDateRef = useRef(null);
    const toDateRef = useRef(null);
    const [entriesPerPage, setEntriesPerPage] = useState('30');
    const [currentPage, setCurrentPage] = useState(1);
    const [editingFollowUp, setEditingFollowUp] = useState(null);
    const [editForm, setEditForm] = useState({
        content: '',
        follow_up_status: '',
        service_type: '',
        assign_to: '',
        date: '',
        time: '',
        serviceMinutes: '0',
        serviceSeconds: '00'
    });

    // Filters
    const [filters, setFilters] = useState({
        dateRangeType: 'dateRange',
        dateRange: 'Last 30 days',
        fromDate: '',
        toDate: '',
        serviceTime: '',
        followUpStatus: ''
    });

    const fetchPatientInfo = async () => {
        try {
            const response = await patientService.getPatientDetails(practiceId, patientId);
            if (response.data?.success) {
                setPatientData(response.data.data.patient);
            }
        } catch (error) {
            console.error('Error fetching patient info:', error);
        }
    };

    const fetchFollowUps = async () => {
        try {
            setLoading(true);
            const response = await patientService.getFollowUps(practiceId, patientId);
            if (response.data?.success) {
                const enriched = (response.data.data || []).map((item, index) => ({
                    ...item,
                    originalIndex: index + 1
                }));
                setResults(enriched);
            } else {
                setResults([]);
            }
        } catch (error) {
            console.error('Error fetching follow-ups:', error);
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPatientInfo();
        fetchFollowUps();
    }, [practiceId, patientId]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => {
            const next = { ...prev, [field]: value };
            // If the user picks a From date that is after the current To date,
            // clear To so the user re-selects a valid (>= From) value.
            if (field === 'fromDate' && value && next.toDate && next.toDate < value) {
                next.toDate = '';
            }
            return next;
        });
        setCurrentPage(1);
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return '';
        return sortConfig.direction === 'asc' ? '▲' : '▼';
    };

    const filteredData = useMemo(() => {
        let filtered = [...results];

        const now = new Date();
        const rowDate = (row) => new Date(row.date || row.created_at || row.date_time);

        if (filters.dateRangeType === 'dateRange' && filters.dateRange) {
            const value = filters.dateRange;
            filtered = filtered.filter((row) => {
                const d = rowDate(row);
                if (Number.isNaN(d.getTime())) return false;
                if (value === 'Last 7 days') {
                    const from = new Date(now);
                    from.setDate(now.getDate() - 7);
                    return d >= from && d <= now;
                }
                if (value === 'Last 30 days') {
                    const from = new Date(now);
                    from.setDate(now.getDate() - 30);
                    return d >= from && d <= now;
                }
                if (value === 'Last 90 days') {
                    const from = new Date(now);
                    from.setDate(now.getDate() - 90);
                    return d >= from && d <= now;
                }
                if (value === 'Last 14 days') {
                    const from = new Date(now);
                    from.setDate(now.getDate() - 14);
                    return d >= from && d <= now;
                }
                if (value === 'All') {
                    return true;
                }
                return true;
            });
        }

        if (filters.dateRangeType === 'fromTo' && (filters.fromDate || filters.toDate)) {
            const from = filters.fromDate ? new Date(filters.fromDate) : null;
            const to = filters.toDate ? new Date(filters.toDate) : null;
            if (to) to.setHours(23, 59, 59, 999);
            filtered = filtered.filter((row) => {
                const d = rowDate(row);
                if (Number.isNaN(d.getTime())) return false;
                if (from && d < from) return false;
                if (to && d > to) return false;
                return true;
            });
        }

        if (filters.followUpStatus) {
            filtered = filtered.filter(row => {
                return normalizeFollowUpStatusKey(row.follow_up_status) === normalizeFollowUpStatusKey(filters.followUpStatus);
            });
        }

        if (filters.serviceTime) {
            const minutesFromRow = (row) => Number.parseInt(row.service_time || row.timerValue || 0, 10) || 0;
            filtered = filtered.filter((row) => {
                const minutes = minutesFromRow(row);
                if (filters.serviceTime === 'All Data') return true;
                if (filters.serviceTime === 'Less than 20 minutes') return minutes < 20;
                if (filters.serviceTime === '99470- Greater than 10 minutes but less than 20 minutes') return minutes >= 10 && minutes < 20;
                if (filters.serviceTime === '99457- Greater than 20 minutes but less than 40 minutes') return minutes >= 20 && minutes < 40;
                if (filters.serviceTime === '99458- greater than 40 minutes but less than 60 minutes') return minutes >= 40 && minutes < 60;
                if (filters.serviceTime === '99458- greater than 60 minutes') return minutes >= 60;
                return true;
            });
        }

        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter((row) => {
                const rowTokens = [
                    String(row.originalIndex ?? ''),
                    formatDateTime(row.date_time || row.date),
                    String(row.caregiver_name || row.caregiver_provider || ''),
                    String(row.service_time || row.timerValue || '6'),
                    String(row.fu_note_content || row.content || ''),
                    getServiceTypeDisplay(row.service_type),
                    renderFollowUpStatus(row.follow_up_status)
                ];
                return rowTokens.some((token) => String(token).toLowerCase().includes(searchLower));
            });
        }

        // Sorting logic
        if (sortConfig.key) {
            filtered.sort((a, b) => {
                let aVal, bVal;

                if (sortConfig.key === 'index') {
                    aVal = Number.parseInt(a.originalIndex ?? 0, 10) || 0;
                    bVal = Number.parseInt(b.originalIndex ?? 0, 10) || 0;
                } else if (sortConfig.key === 'date_time') {
                    // Combine date and time for accurate sorting
                    const parseDate = (item) => {
                        const dStr = item.date ? item.date.split('T')[0] : (item.created_at ? item.created_at.split('T')[0] : '');
                        const tStr = item.time || '00:00:00';
                        if (!dStr) return 0;
                        // Robust parsing: YYYY-MM-DD HH:mm:ss
                        const d = new Date(`${dStr}T${tStr.includes(':') ? tStr : tStr + ':00'}`);
                        return isNaN(d.getTime()) ? new Date(dStr).getTime() || 0 : d.getTime();
                    };
                    aVal = parseDate(a);
                    bVal = parseDate(b);
                } else if (sortConfig.key === 'caregiver') {
                    aVal = (a.caregiver_name || a.caregiver_provider || '').toLowerCase().trim();
                    bVal = (b.caregiver_name || b.caregiver_provider || '').toLowerCase().trim();
                } else if (sortConfig.key === 'service_time') {
                    aVal = Number.parseInt(a.service_time || a.timerValue || 0, 10) || 0;
                    bVal = Number.parseInt(b.service_time || b.timerValue || 0, 10) || 0;
                } else if (sortConfig.key === 'notes') {
                    aVal = (a.fu_note_content || a.content || '').toLowerCase().trim();
                    bVal = (b.fu_note_content || b.content || '').toLowerCase().trim();
                } else if (sortConfig.key === 'service_type') {
                    aVal = getServiceTypeDisplay(a.service_type).toLowerCase().trim();
                    bVal = getServiceTypeDisplay(b.service_type).toLowerCase().trim();
                } else if (sortConfig.key === 'status') {
                    aVal = renderFollowUpStatus(a.follow_up_status).toLowerCase().trim();
                    bVal = renderFollowUpStatus(b.follow_up_status).toLowerCase().trim();
                }

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return filtered;
    }, [results, filters, searchTerm, sortConfig]);

    const pageSize = entriesPerPage === 'all' ? Math.max(filteredData.length, 1) : parseInt(entriesPerPage, 10);
    const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
    const paginatedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const patientName = patientData
        ? `${patientData.last_name || ''}, ${patientData.first_name || ''}`.trim()
        : '...';

    const dob = patientData?.date_of_birth ? formatDateShort(patientData.date_of_birth) : '...';
    const practiceName = patientData?.practice_name || patientData?.practice || 'Practice';

    useEffect(() => {
        if (!patientData) return;
        const patientLabel = patientName && patientName !== '...' ? patientName : 'Patient';
        updateBreadcrumbs([
            { label: 'Practices', path: '/practices' },
            { label: practiceName, path: `/practices/${practiceId}` },
            { label: 'Patients', path: `/practices/${practiceId}?tab=patient` },
            { label: patientLabel, path: `/practices/${practiceId}/patients/${patientId}` },
            { label: 'Follow Up List', path: null, active: true }
        ]);
    }, [patientData, patientName, practiceId, patientId, practiceName, updateBreadcrumbs]);

    // --- Actions Implementations --- //

    const handlePrint = () => {
        if (filteredData.length === 0) {
            toast.warning('No data to print');
            return;
        }

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            toast.error('Please allow popups to print');
            return;
        }

        const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Follow Up List Report</title>
          <style>
            @media print {
              @page {
                margin: 0.5in;
                size: letter landscape;
              }
              body {
                font-family: Arial, sans-serif;
                font-size: 10px;
                margin: 0;
                padding: 0;
              }
              .print-header {
                text-align: center;
                margin-bottom: 20px;
                border-bottom: 2px solid #000;
                padding-bottom: 10px;
              }
              .print-header h2 { margin: 0 0 5px 0; font-size: 18px; }
              .patient-info { display: flex; justify-content: space-between; margin-bottom: 15px; font-weight: bold; font-size: 12px;}
              .print-date { text-align: right; font-size: 10px; margin-bottom: 10px; }
              table { width: 100%; border-collapse: collapse; font-size: 10px; table-layout: fixed; }
              th, td { border: 1px solid #000; padding: 6px; text-align: left; word-wrap: break-word;}
              th { background-color: #f0f0f0; font-weight: bold; }
              tr:nth-child(even) { background-color: #f9f9f9; }
              
              th:nth-child(1) { width: 5%; }
              th:nth-child(2) { width: 12%; }
              th:nth-child(3) { width: 15%; }
              th:nth-child(4) { width: 10%; }
              th:nth-child(5) { width: 35%; }
              th:nth-child(6) { width: 10%; }
              th:nth-child(7) { width: 13%; }
            }
          </style>
        </head>
        <body>
          <div class="print-header"><h2>Follow Up List Report</h2></div>
          <div class="patient-info">
            <div>Patient: ${patientName}</div>
            <div>DOB: ${dob}</div>
          </div>
          <div class="print-date">Generated: ${new Date().toLocaleString()}</div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Date &amp; Time</th>
                <th>Caregiver</th>
                <th>Service Time (Min)</th>
                <th>Follow Up Notes</th>
                <th>Service Type</th>
                <th>Follow Up Status</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map((result, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${formatDateTime(result.date_time || result.date)}</td>
                  <td>${result.caregiver_provider || 'N/A'}</td>
                  <td>${result.service_time || result.timerValue || '6'}</td>
                  <td>${(result.fu_note_content || result.content || 'N/A').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>
                  <td>${getServiceTypeDisplay(result.service_type)}</td>
                  <td>${renderFollowUpStatus(result.follow_up_status)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div style="margin-top: 20px; font-size: 10px; text-align: center;">Total Records: ${filteredData.length}</div>
        </body>
      </html>
    `;

        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();

        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 250);
    };

    const handleExportPDF = async () => {
        if (filteredData.length === 0) {
            toast.warning('No data to export');
            return;
        }
        setLoading(true);

        try {
            const element = document.createElement('div');
            element.style.position = 'absolute';
            element.style.left = '-9999px';
            element.style.top = '0';
            element.style.width = '11in';
            element.style.zIndex = '-1';

            element.innerHTML = `
        <div style="padding: 40px; font-family: 'Segoe UI', sans-serif; color: #333; background: #fff;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; border-bottom: 2px solid #165a57; padding-bottom: 15px;">
            <div style="display: flex; align-items: center;">
              <h1 style="margin: 0; font-size: 24px; color: #165a57; font-weight: 700;">codexterlab</h1>
            </div>
            <div style="text-align: right;">
              <h2 style="margin: 0; font-size: 18px; color: #2c3e50;">Follow Up List</h2>
              <div style="font-size: 12px; color: #666; margin-top: 5px;">Patient: <strong>${patientName}</strong> | DOB: <strong>${dob}</strong></div>
              <div style="font-size: 11px; color: #666; margin-top: 5px;">Generated: ${new Date().toLocaleString()}</div>
            </div>
          </div>
          <table style="width: 100%; border-collapse: collapse; font-size: 10px; table-layout: fixed;">
            <thead>
              <tr style="background-color: #165a57; color: #ffffff;">
                <th style="padding: 8px; text-align: left; width: 4%;">#</th>
                <th style="padding: 8px; text-align: left; width: 14%;">Date &amp; Time</th>
                <th style="padding: 8px; text-align: left; width: 15%;">Caregiver</th>
                <th style="padding: 8px; text-align: left; width: 10%;">Time (Min)</th>
                <th style="padding: 8px; text-align: left; width: 35%;">Notes</th>
                <th style="padding: 8px; text-align: left; width: 10%;">Type</th>
                <th style="padding: 8px; text-align: left; width: 12%;">Status</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map((result, idx) => `
                <tr style="background-color: ${idx % 2 === 0 ? '#ffffff' : '#f8f9fa'};">
                  <td style="border: 1px solid #e9ecef; padding: 6px; vertical-align: top;">${idx + 1}</td>
                  <td style="border: 1px solid #e9ecef; padding: 6px; vertical-align: top; white-space: nowrap;">${formatDateTime(result.date_time || result.date)}</td>
                  <td style="border: 1px solid #e9ecef; padding: 6px; vertical-align: top;">${(result.caregiver_provider || 'N/A').replace(/"/g, '&quot;')}</td>
                  <td style="border: 1px solid #e9ecef; padding: 6px; vertical-align: top;">${result.service_time || result.timerValue || '6'}</td>
                  <td style="border: 1px solid #e9ecef; padding: 6px; vertical-align: top; max-width: 350px;">
                    <div style="max-height: 100px; overflow: hidden; line-height: 1.4;">
                      ${(result.fu_note_content || result.content || 'N/A').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')}
                    </div>
                  </td>
                  <td style="border: 1px solid #e9ecef; padding: 6px; vertical-align: top;">${getServiceTypeDisplay(result.service_type)}</td>
                  <td style="border: 1px solid #e9ecef; padding: 6px; vertical-align: top;">${renderFollowUpStatus(result.follow_up_status)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div style="margin-top: 20px; text-align: center; font-size: 10px; color: #666;">
            Total Records: ${filteredData.length}
          </div>
        </div>
      `;

            document.body.appendChild(element);
            await new Promise(resolve => setTimeout(resolve, 300));
            const opt = {
                margin: [0.3, 0.3, 0.3, 0.3],
                filename: `Follow-Up-List-${patientName.replace(/\\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, logging: false },
                jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' }
            };

            await html2pdf().set(opt).from(element).save();
            document.body.removeChild(element);
            toast.success('Report exported to PDF successfully');
        } catch (error) {
            console.error('Error exporting PDF:', error);
            toast.error('Failed to export PDF');
        } finally {
            setLoading(false);
        }
    };

    const generateEmailContent = (data) => {
        if (!data || data.length === 0) return 'No follow-up data available.';
        const reportText = data.map((result, idx) => {
            return `${idx + 1}. Date: ${formatDateTime(result.date_time || result.date)}\n   Caregiver: ${result.caregiver_provider || 'N/A'}\n   Time: ${result.service_time || result.timerValue || '6'} min\n   Status: ${renderFollowUpStatus(result.follow_up_status)}\n   Note: ${(result.fu_note_content || result.content || 'N/A').substring(0, 200)}...\n`;
        }).join('\n');
        return `Follow Up List Report\nPatient: ${patientName} (DOB: ${dob})\nGenerated: ${new Date().toLocaleString()}\nTotal Records: ${data.length}\n\n${reportText}`;
    };

    const handleSendEmail = async (emailData) => {
        try {
            setEmailSending(true);
            const emailPayload = {
                to: emailData.to,
                cc: emailData.cc || '',
                bcc: emailData.bcc || '',
                subject: emailData.subject,
                content: emailData.content,
                filters: { practiceId, patientId }
            };

            if (emailData.attachments && emailData.attachments.length > 0) {
                const formData = new FormData();
                Object.keys(emailPayload).forEach(key => {
                    if (key === 'filters') formData.append(key, JSON.stringify(emailPayload[key]));
                    else formData.append(key, emailPayload[key]);
                });
                emailData.attachments.forEach(file => formData.append('attachments', file));

                const axios = require('axios');
                const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
                const response = await axios.post(`${API_URL}/care-management/follow-up-query/email`, formData, {
                    withCredentials: true,
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                if (response.data?.success) {
                    toast.success('Email sent successfully');
                } else {
                    toast.error(response.data?.message || 'Failed to send email');
                }
            } else {
                const response = await careManagementService.emailFollowUpQuery(emailPayload.filters, emailData);
                if (response.data?.success) {
                    toast.success('Email sent successfully');
                } else {
                    toast.error(response.data?.message || 'Failed to send email');
                }
            }
            setShowEmailModal(false);
        } catch (error) {
            console.error('Error sending email:', error);
            toast.error('Failed to send email');
        } finally {
            setEmailSending(false);
        }
    };

    const openDatePicker = (ref) => {
        const el = ref?.current;
        if (!el) return;
        try {
            el.showPicker?.();
        } catch {
            el.focus();
        }
    };

    const handleEditFollowUp = async (row) => {
        const followUpId = row?.id;
        if (!followUpId) {
            toast.error('Follow-up ID not found');
            return;
        }
        const timerParts = splitTimerValue(row);
        setEditingFollowUp(row);
        setEditForm({
            content: row?.fu_note_content || row?.content || '',
            follow_up_status: normalizeStatusForEdit(row?.follow_up_status),
            service_type: getServiceTypeDisplay(row?.service_type),
            assign_to: row?.caregiver_name || row?.caregiver_provider || 'N/A',
            date: toDateInputValue(row?.date || row?.created_at || row?.date_time),
            time: normalizeTimeInput(row?.date_time || row?.time),
            serviceMinutes: timerParts.minutes,
            serviceSeconds: timerParts.seconds
        });
    };

    const handleDeleteFollowUp = async (row) => {
        const followUpId = row?.id;
        if (!followUpId) {
            toast.error('Follow-up ID not found');
            return;
        }
        if (!window.confirm('Are you sure you want to delete this follow-up note?')) return;

        try {
            const response = await patientService.deleteFollowUp(practiceId, patientId, followUpId);
            if (response.data?.success) {
                toast.success('Follow-up deleted successfully');
                fetchFollowUps();
            } else {
                toast.error(response.data?.message || 'Failed to delete follow-up');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete follow-up');
        }
    };

    const handleSaveEdit = async () => {
        if (!editingFollowUp?.id) return;
        if (!editForm.content.trim()) {
            toast.error('Follow-up note cannot be empty');
            return;
        }
        const safeMinutes = Math.max(0, Number.parseInt(editForm.serviceMinutes || '0', 10) || 0);
        const safeSeconds = Math.min(59, Math.max(0, Number.parseInt(editForm.serviceSeconds || '0', 10) || 0));
        const timerValue = `${String(safeMinutes).padStart(2, '0')}:${String(safeSeconds).padStart(2, '0')}`;
        try {
            const response = await patientService.updateFollowUp(practiceId, patientId, editingFollowUp.id, {
                content: editForm.content.trim(),
                follow_up_status: editForm.follow_up_status,
                date: editForm.date,
                time: editForm.time,
                timerValue
            });
            if (response.data?.success) {
                toast.success('Follow-up updated successfully');
                setEditingFollowUp(null);
                fetchFollowUps();
            } else {
                toast.error(response.data?.message || 'Failed to update follow-up');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update follow-up');
        }
    };

    return (
        <div className="follow-up-list-wrapper">
            <div className="follow-up-list-header">
                <div className="follow-up-list-title-area">
                    <h2 className="follow-up-list-title">Follow Up List</h2>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div className="patient-info-block">
                        <div className="patient-info-row">
                            <span className="patient-info-label">Name</span>
                            <span className="patient-info-colon">:</span>
                            <span className="patient-info-value">{patientName}</span>
                        </div>
                        <div className="patient-info-row">
                            <span className="patient-info-label">DOB</span>
                            <span className="patient-info-colon">:</span>
                            <span className="patient-info-value">{dob}</span>
                        </div>
                    </div>
                    <BackButton to={`/practices/${practiceId}/patients/${patientId}`} />
                </div>
            </div>

            <div className="query-period-card">
                <div className="query-period-title">Query Period</div>

                <div className="filter-grid-layout">
                    {/* Left Column */}
                    <div className="filter-left-col">
                        <div className="date-row-filter">
                            <label className="radio-label radio-only">
                                <input
                                    type="radio"
                                    name="dateRangeType"
                                    checked={filters.dateRangeType === 'dateRange'}
                                    onChange={() => handleFilterChange('dateRangeType', 'dateRange')}
                                />
                            </label>
                            <div className="date-input-group">
                                <label>Date Range</label>
                                <select
                                    className="form-select-fl"
                                    value={filters.dateRange}
                                    onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                                    disabled={filters.dateRangeType !== 'dateRange'}
                                >
                                    <option value="">All Data</option>
                                    <option value="Last 7 days">Last 7 days</option>
                                    <option value="Last 14 days">Last 14 days</option>
                                    <option value="Last 30 days">Last 30 days</option>
                                    <option value="Last 90 days">Last 90 days</option>
                                    <option value="All">All</option>
                                </select>
                            </div>
                            <span />
                        </div>

                        <div className="date-row-filter">
                            <label className="radio-label radio-only">
                                <input
                                    type="radio"
                                    name="dateRangeType"
                                    checked={filters.dateRangeType === 'fromTo'}
                                    onChange={() => handleFilterChange('dateRangeType', 'fromTo')}
                                />
                            </label>
                            <div className="date-input-group">
                                <label>From</label>
                                <input
                                    ref={fromDateRef}
                                    type="date"
                                    className="form-input-fl"
                                    value={filters.fromDate}
                                    max={filters.toDate || undefined}
                                    onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                    disabled={filters.dateRangeType !== 'fromTo'}
                                    onClick={() => {
                                        if (filters.dateRangeType !== 'fromTo') return;
                                        openDatePicker(fromDateRef);
                                    }}
                                />
                            </div>
                            <div className="date-input-group">
                                <label>To</label>
                                <input
                                    ref={toDateRef}
                                    type="date"
                                    className="form-input-fl"
                                    value={filters.toDate}
                                    min={filters.fromDate || undefined}
                                    onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                    disabled={filters.dateRangeType !== 'fromTo' || !filters.fromDate}
                                    onClick={() => {
                                        if (filters.dateRangeType !== 'fromTo') return;
                                        openDatePicker(toDateRef);
                                    }}
                                    title={!filters.fromDate ? 'Select a From date first' : ''}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="filter-right-col">
                        <div className="form-field-filter">
                            <label>Service Time</label>
                            <div style={{ position: 'relative' }}>
                                <select
                                    className="form-select-fl"
                                    value={filters.serviceTime}
                                    onChange={(e) => handleFilterChange('serviceTime', e.target.value)}
                                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '12px 12px', appearance: 'none' }}
                                >
                                    <option value="">Select Service Time</option>
                                    <option value="All Data">All Data</option>
                                    <option value="Less than 20 minutes">Less than 20 minutes</option>
                                    <option value="99470- Greater than 10 minutes but less than 20 minutes">99470- Greater than 10 minutes but less than 20 minutes</option>
                                    <option value="99457- Greater than 20 minutes but less than 40 minutes">99457- Greater than 20 minutes but less than 40 minutes</option>
                                    <option value="99458- greater than 40 minutes but less than 60 minutes">99458- greater than 40 minutes but less than 60 minutes</option>
                                    <option value="99458- greater than 60 minutes">99458- greater than 60 minutes</option>
                                </select>
                            </div>
                        </div>
                        <div className="form-field-filter follow-up-status-filter">
                            <label>Follow Up Status</label>
                            <div style={{ position: 'relative' }}>
                                <select
                                    className="form-select-fl"
                                    value={filters.followUpStatus}
                                    onChange={(e) => handleFilterChange('followUpStatus', e.target.value)}
                                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '12px 12px', appearance: 'none' }}
                                >
                                    <option value="">Select Follow Up Status</option>
                                    <option value="no_need_to_follow_up">No need to follow up</option>
                                    <option value="continue_follow_up">Continue follow up</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-actions-fl">
                    <button className="btn-fl" onClick={() => setShowAddFollowUpModal(true)}>Add Data</button>
                    <button className="btn-fl" onClick={handlePrint} disabled={loading || filteredData.length === 0}>Print</button>
                    <button className="btn-fl" onClick={handleExportPDF} disabled={loading || filteredData.length === 0}>Export</button>
                    <button className="btn-fl" onClick={() => setShowEmailModal(true)} disabled={loading || filteredData.length === 0}>Email</button>
                </div>
            </div>

            <div className="table-toolbar-fl">
                <ListScreenSearchBar
                    value={searchTerm}
                    onChange={setSearchTerm}
                    inputId="follow-up-list-search"
                />
            </div>

            <div className="table-wrap-fl">
                <table className="follow-up-table-fl">
                    <colgroup>
                        <col className="follow-up-col-idx" />
                        <col className="follow-up-col-datetime" />
                        <col className="follow-up-col-caregiver" />
                        <col className="follow-up-col-service-time" />
                        <col className="follow-up-col-notes" />
                        <col className="follow-up-col-service-type" />
                        <col className="follow-up-col-status" />
                        <col className="follow-up-col-action" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('index')} style={{ cursor: 'pointer' }}>
                                <span className="follow-up-sort-header-label"># {sortConfig.key === 'index' ? <span className="follow-up-sort-indicator active">{getSortIndicator('index')}</span> : null}</span>
                            </th>
                            <th onClick={() => handleSort('date_time')} style={{ cursor: 'pointer' }}>
                                <span className="follow-up-sort-header-label">Date &amp; Time {sortConfig.key === 'date_time' ? <span className="follow-up-sort-indicator active">{getSortIndicator('date_time')}</span> : null}</span>
                            </th>
                            <th onClick={() => handleSort('caregiver')} style={{ cursor: 'pointer' }}>
                                <span className="follow-up-sort-header-label">Caregiver {sortConfig.key === 'caregiver' ? <span className="follow-up-sort-indicator active">{getSortIndicator('caregiver')}</span> : null}</span>
                            </th>
                            <th onClick={() => handleSort('service_time')} style={{ cursor: 'pointer' }}>
                                <span className="follow-up-sort-header-label">Service Time (Min) {sortConfig.key === 'service_time' ? <span className="follow-up-sort-indicator active">{getSortIndicator('service_time')}</span> : null}</span>
                            </th>
                            <th className="follow-up-notes-header" onClick={() => handleSort('notes')} style={{ cursor: 'pointer' }}>
                                <span className="follow-up-sort-header-label">Follow Up Notes {sortConfig.key === 'notes' ? <span className="follow-up-sort-indicator active">{getSortIndicator('notes')}</span> : null}</span>
                            </th>
                            <th onClick={() => handleSort('service_type')} style={{ cursor: 'pointer' }}>
                                <span className="follow-up-sort-header-label">Service Type {sortConfig.key === 'service_type' ? <span className="follow-up-sort-indicator active">{getSortIndicator('service_type')}</span> : null}</span>
                            </th>
                            <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                                <span className="follow-up-sort-header-label">Follow Up Status {sortConfig.key === 'status' ? <span className="follow-up-sort-indicator active">{getSortIndicator('status')}</span> : null}</span>
                            </th>
                            <th style={{ textAlign: 'center' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            Array.from({ length: 8 }, (_, skIdx) => (
                                <tr key={`sk-${skIdx}`} className="reusable-table-skeleton-row">
                                    {Array.from({ length: 8 }, (_, tdIdx) => (
                                        <td key={tdIdx}>
                                            <div className="reusable-table-skeleton-cell" />
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : filteredData.length > 0 ? (
                            paginatedData.map((row, idx) => (
                                <tr key={idx}>
                                    <td>{((currentPage - 1) * pageSize) + idx + 1}</td>
                                    <td>{formatDateTime(row.date_time || row.date)}</td>
                                    <td>{row.caregiver_name || row.caregiver_provider || 'N/A'}</td>
                                    <td>{row.service_time || row.timerValue || '6'}</td>
                                    <td className="follow-up-notes-cell">
                                        {row.fu_note_content || row.content || 'N/A'}
                                    </td>
                                    <td>{getServiceTypeDisplay(row.service_type)}</td>
                                    <td>{renderFollowUpStatus(row.follow_up_status)}</td>
                                    <td>
                                        <div className="action-icons-fl">
                                            <span
                                                className="action-icon-fl edit"
                                                onClick={() => handleEditFollowUp(row)}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                <FontAwesomeIcon icon={faEdit} />
                                            </span>
                                            <span
                                                className="action-icon-fl delete"
                                                onClick={() => handleDeleteFollowUp(row)}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" style={{ textAlign: 'center' }}>No follow ups found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <ListScreenPaginationBar
                entriesValue={entriesPerPage}
                onEntriesChange={(value) => {
                    setEntriesPerPage(value);
                    setCurrentPage(1);
                }}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                showPageSelector={entriesPerPage !== 'all'}
            />

            {/* Modals */}
            <EmailModal
                isOpen={showEmailModal}
                onClose={() => setShowEmailModal(false)}
                onSend={handleSendEmail}
                defaultSubject={`Follow Up List Report - ${new Date().toLocaleDateString()}`}
                defaultContent={generateEmailContent(filteredData)}
            />

            <AddFollowUpModal
                isOpen={showAddFollowUpModal}
                onClose={() => setShowAddFollowUpModal(false)}
                practiceId={practiceId}
                patientId={patientId}
                patientName={patientName}
                onSuccess={() => {
                    setShowAddFollowUpModal(false);
                    fetchFollowUps();
                }}
            />

            {editingFollowUp && (
                <div className="follow-up-edit-overlay" onClick={() => setEditingFollowUp(null)}>
                    <div className="follow-up-edit-card" onClick={(e) => e.stopPropagation()}>
                        <h4>Edit Follow Up</h4>
                        <div className="follow-up-edit-grid">
                            <div className="follow-up-edit-field">
                                <label>Service Type</label>
                                <input className="form-input-fl" value={editForm.service_type} disabled />
                            </div>
                            <div className="follow-up-edit-field">
                                <label>Follow Up Status</label>
                                <input className="form-input-fl" value={renderFollowUpStatus(editForm.follow_up_status)} disabled />
                            </div>
                            <div className="follow-up-edit-field">
                                <label>Assign To</label>
                                <input className="form-input-fl" value={editForm.assign_to} disabled />
                            </div>
                            <div className="follow-up-edit-field">
                                <label>Service Time (MM:SS)</label>
                                <div className="follow-up-edit-timer-row">
                                    <input
                                        className="form-input-fl"
                                        type="number"
                                        min="0"
                                        value={editForm.serviceMinutes}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, serviceMinutes: e.target.value }))}
                                    />
                                    <span>:</span>
                                    <input
                                        className="form-input-fl"
                                        type="number"
                                        min="0"
                                        max="59"
                                        value={editForm.serviceSeconds}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, serviceSeconds: e.target.value }))}
                                    />
                                </div>
                            </div>
                            <div className="follow-up-edit-field">
                                <label>Date</label>
                                <input
                                    className="form-input-fl"
                                    type="date"
                                    value={editForm.date}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, date: e.target.value }))}
                                />
                            </div>
                            <div className="follow-up-edit-field">
                                <label>Time</label>
                                <input
                                    className="form-input-fl"
                                    type="time"
                                    value={editForm.time}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, time: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div className="follow-up-edit-field">
                            <label>Follow Up Note</label>
                            <textarea
                                value={editForm.content}
                                onChange={(e) => setEditForm((prev) => ({ ...prev, content: e.target.value }))}
                                rows={5}
                            />
                        </div>
                        <div className="follow-up-edit-actions">
                            <button className="btn-fl" onClick={() => setEditingFollowUp(null)}>Cancel</button>
                            <button className="btn-fl" onClick={handleSaveEdit}>Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FollowUpList;
